import { useEffect, useState } from 'react';
import { RotateCcw, Layers } from 'lucide-react';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { safeInvoke } from '../lib/safeInvoke';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../hooks/useAuth';
import { ToolPageShell } from '../components/tool';

// ─── Types ────────────────────────────────────────────────────────────
interface TarotCard {
  name: string;
  namePt: string;
  arcana: 'major' | 'minor';
  reversed: boolean;
  position: string;
}
interface TarotResult {
  cards: TarotCard[];
  interpretation: string;
}

type Spread = 'single' | 'three' | 'celtic' | 'horseshoe';

// ─── Single card component ────────────────────────────────────────────
function TarotCardDisplay({ card, revealed }: { card: TarotCard; revealed: boolean }) {
  const [flipped,        setFlipped]        = useState(false);
  const [showInterpret,  setShowInterpret]  = useState(false);

  useEffect(() => {
    if (revealed) {
      setTimeout(() => setFlipped(true), 300);
      setTimeout(() => setShowInterpret(true), 1500);
    }
  }, [revealed]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', minWidth: '100px' }}>
      {/* Card flip container */}
      <div style={{ perspective: '600px', width: '80px', height: '130px', flexShrink: 0 }}>
        <div style={{
          position: 'relative', width: '100%', height: '100%',
          transition: 'transform 0.7s ease',
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}>
          {/* Card back */}
          <div style={{
            position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
            backgroundColor: 'var(--bg-elevated)', border: '2px solid var(--border-gold)',
            borderRadius: 'var(--radius-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: '1.75rem', opacity: 0.6 }}>✦</span>
          </div>
          {/* Card face */}
          <div style={{
            position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
            backgroundColor: 'var(--bg-surface)', border: '2px solid var(--border-gold)',
            borderRadius: 'var(--radius-md)', padding: '0.5rem',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
            transform: card.reversed ? 'rotateY(180deg) rotate(180deg)' : 'rotateY(180deg)',
          }}>
            <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{card.arcana === 'major' ? '⭐' : '🃏'}</span>
            <p style={{ fontSize: '8px', color: 'var(--text-primary)', textAlign: 'center', margin: 0, fontWeight: 600, lineHeight: 1.3 }}>
              {card.namePt}
            </p>
            {card.reversed && (
              <p style={{ fontSize: '7px', color: 'var(--petrol)', margin: 0 }}>Invertida</p>
            )}
          </div>
        </div>
      </div>

      {/* Position label */}
      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-tertiary)', textAlign: 'center', maxWidth: '100px' }}>
        {card.position}
      </p>

      {/* Card name slides in */}
      {showInterpret && (
        <p className="animate-fade-in" style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', textAlign: 'center', fontWeight: 600 }}>
          {card.namePt}
        </p>
      )}
    </div>
  );
}

// ─── Intention moment ─────────────────────────────────────────────────
function IntentionMoment({ onReady }: { onReady: () => void }) {
  const { t } = useI18n();
  const [count, setCount] = useState(3);
  useEffect(() => {
    const timer = setInterval(() => setCount(c => {
      if (c <= 1) { clearInterval(timer); onReady(); return 0; }
      return c - 1;
    }), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <p className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        {t('pages.tarot.intentionMsg')}
      </p>
      <span style={{ fontSize: '3rem', color: 'var(--petrol)' }} className="animate-fade-in">{count || '✦'}</span>
    </div>
  );
}

// ─── Main TarotPage ───────────────────────────────────────────────────
const LOADING_PHASES = [
  'Embaralhando as cartas...',
  'Lendo a energia do momento...',
  'Interpretando os arcanos...',
  'Revelando a mensagem das cartas...',
];

export function TarotPage() {
  useSystemUsageTracker('tarot');
  const { t, lang } = useI18n();
  const { profile } = useAuth();
  const SPREADS: { key: Spread; label: string; emoji: string; desc: string; count: number }[] = [
    { key: 'single',    label: t('pages.tarot.spreadSingle'),    emoji: '🃏', desc: t('pages.tarot.spreadSingleDesc'),    count: 1 },
    { key: 'three',     label: t('pages.tarot.spreadThree'),     emoji: '🎴', desc: t('pages.tarot.spreadThreeDesc'),     count: 3 },
    { key: 'horseshoe', label: t('pages.tarot.spreadHorseshoe'), emoji: '🧿', desc: t('pages.tarot.spreadHorseshoeDesc'), count: 7 },
    { key: 'celtic',    label: t('pages.tarot.spreadCeltic'),    emoji: '✨', desc: t('pages.tarot.spreadCelticDesc'),    count: 10 },
  ];
  const [spread,       setSpread]      = useState<Spread | null>(null);
  const [phase,        setPhase]       = useState<'select'|'intention'|'loading'|'reveal'|'done'>('select');
  const [result,       setResult]      = useState<TarotResult | null>(null);
  const [revealedCount,setRevealedCount]= useState(0);

  useEffect(() => {
    if (result) { setResult(null); setPhase('select'); setSpread(null); setRevealedCount(0); }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSelectSpread(s: Spread) {
    setSpread(s);
    setPhase('intention');
  }

  async function fetchReading() {
    setPhase('loading');
    const userName = profile?.full_name?.split(' ')[0] ?? undefined;
    const { data } = await safeInvoke<TarotResult>('tarot-reading', {
      body:     { spread, userName },
      fallback: { cards: [], interpretation: '' },
    });
    setResult(data);
    setPhase('reveal');
    // Reveal cards one by one
    if (data.cards.length > 0) {
      for (let i = 1; i <= data.cards.length; i++) {
        await new Promise(r => setTimeout(r, 800));
        setRevealedCount(i);
      }
      await new Promise(r => setTimeout(r, 600));
      setPhase('done');
    }
  }

  function restart() {
    setSpread(null);
    setPhase('select');
    setResult(null);
    setRevealedCount(0);
  }

  return (
    <ToolPageShell
      icon={Layers}
      title={t('pages.tarot.title')}
      subtitle={t('pages.tarot.subtitle')}
      headerRight={phase !== 'select' ? (
        <button className="btn btn-ghost btn-sm" onClick={restart}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <RotateCcw size={15} aria-hidden /> {t('pages.tarot.restart')}
        </button>
      ) : undefined}
    >

      {/* Spread selection */}
      {phase === 'select' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
          {SPREADS.map(s => (
            <button key={s.key} className="card" onClick={() => handleSelectSpread(s.key)}
              style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', transition: 'border-color 200ms' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--border-gold)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-subtle)')}>
              <span style={{ fontSize: '1.75rem', display: 'block', marginBottom: '0.5rem' }}>{s.emoji}</span>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: '0 0 0.25rem' }}>{s.label}</p>
              <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', margin: 0 }}>{s.desc}</p>
            </button>
          ))}
        </div>
      )}

      {/* Intention moment */}
      {phase === 'intention' && <IntentionMoment onReady={fetchReading} />}

      {/* Loading */}
      {phase === 'loading' && (
        <div className="card">
          <PhaseLoader phases={LOADING_PHASES} />
        </div>
      )}

      {/* Card reveal */}
      {(phase === 'reveal' || phase === 'done') && result && (
        <>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center',
            padding: '1.5rem', backgroundColor: 'var(--bg-surface)',
            borderRadius: 'var(--radius-card)', marginBottom: '1.5rem',
            border: '1px solid var(--border-gold)',
          }}>
            {result.cards.map((card, i) => (
              <TarotCardDisplay key={i} card={card} revealed={i < revealedCount} />
            ))}
          </div>

          {/* Interpretation */}
          {phase === 'done' && result.interpretation && (
            <div className="card animate-fade-in">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '1rem' }}>
                {t('pages.tarot.generalReading')}
              </p>
              <div className="reading-text">
                {result.interpretation.split('\n').filter(Boolean).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              <ReadingActions pageName="Tarot de Marselha" reading={result.interpretation} signEmoji="🃏" />
              <ReadingFeedback feature="tarot-reading" />
            </div>
          )}
        </>
      )}
    </ToolPageShell>
  );
}
